// The native Printt apps (iOS / Android) announce what they can do by setting window.printtNativeApp
// before the page loads, e.g. { version: 1, features: ['googleSignIn', 'appleSignIn'] }.
// The older Flutter app sets nothing, so features it can't handle (Google, Apple) stay hidden there.
// locationCoords: the app asks for location itself and sends the phone's coordinates back, so the
// website neither shows its own location dialog nor triggers the browser's location prompt
// directions: the app opens directions in the phone's maps app (asking which one if there are several)
export type NativeAppFeature = 'googleSignIn' | 'appleSignIn' | 'locationCoords' | 'directions';

export const nativeAppSupports = (feature: NativeAppFeature): boolean =>
  !!window.printtNativeApp?.features?.includes(feature);

export type DirectionsTarget = { latitude: number; longitude: number; name?: string };

// Directions are only offered where they open properly: the website, or a native app that handles them.
// The older Flutter app would open the maps page inside its own web view, with no way back.
export const canOpenDirections = () => !window.isMobileApp || nativeAppSupports('directions');

// On the desktop website, Delivery and Click & Collect orders are one page (options, then the address or shop, with the
// order summary beside them) followed by Checkout. Phones in the browser and the apps go step by step instead (see
// ORDER_STEPS). 970px: the theme's md breakpoint, where useIsDesktop starts
export const orderOnOnePage = () => !window.isMobileApp && window.matchMedia('(min-width: 970px)').matches;

// The order's steps (the order's instantQuoteStep). The numbers are kept from the older flow, so they aren't in
// order: step by step (phones, the apps) the order runs upload → service → where → options → checkout; on one page
// upload → options → checkout
export const ORDER_STEPS = { upload: 0, options: 1, where: 2, checkout: 3, service: 4 } as const;

// The step after the files are in: choosing the service (step by step), or the print options (one page)
export const stepAfterUpload = () => (orderOnOnePage() ? ORDER_STEPS.options : ORDER_STEPS.service);

// Self Service, once a printer's QR code is scanned: from the printer step on to the print options; at checkout it
// stays there, ready to pay
export const stepAfterPrinterScan = (current: number) =>
  current === ORDER_STEPS.checkout ? ORDER_STEPS.checkout : ORDER_STEPS.options;

// A step's place in the order, to tell moving forward from going back
export const stepRank = (step: number) => {
  const { upload, service, where, options, checkout } = ORDER_STEPS;
  const order: number[] = orderOnOnePage()
    ? [upload, options, where, checkout]
    : [upload, service, where, options, checkout];
  return order.indexOf(step);
};

// iPhones and iPads (iPadOS reports itself as a Mac, but with touch)
const isAppleMobile = () =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) ||
  (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

// Directions in the visitor's own maps app instead of a route drawn on our map
export const openDirections = (target: DirectionsTarget) => {
  if (window.isMobileApp && nativeAppSupports('directions')) {
    window.mobileManager.requestDirections(target);
    return;
  }
  const { latitude, longitude, name } = target;
  // On a phone these open Apple Maps, or the Google Maps app when it's installed
  const url = isAppleMobile()
    ? `https://maps.apple.com/?daddr=${latitude},${longitude}${name ? `&q=${encodeURIComponent(name)}` : ''}`
    : `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
  window.open(url, '_blank', 'noopener');
};

// The shop's map position, if it has one
export const shopDirectionsTarget = (shop: any): DirectionsTarget | null => {
  const latitude = Number(shop?.full_address?.lat ?? shop?.lat);
  const longitude = Number(shop?.full_address?.lon ?? shop?.lon);
  if (!shop || !Number.isFinite(latitude) || !Number.isFinite(longitude) || (!latitude && !longitude)) return null;
  return { latitude, longitude, name: shop.name };
};

// Which social sign-in buttons to show: all of them on the website, only what the app supports in the app
export const socialSignInOptions = () => {
  if (!window.isMobileApp) return { google: true, apple: true };
  return {
    google: nativeAppSupports('googleSignIn'),
    apple: isIOS() && nativeAppSupports('appleSignIn'),
  };
};

// The order page's card to bring into view when it opens, e.g. after Edit at checkout (desktop website: files, the
// address / shop, and the print options are all on the options page)
export type OrderSection = 'files' | 'service' | 'options';
let orderSectionToShow: OrderSection | null = null;
export const showOrderSection = (section: OrderSection) => {
  orderSectionToShow = section;
};
export const takeOrderSection = () => {
  const section = orderSectionToShow;
  orderSectionToShow = null;
  return section;
};
